import { useMemo } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import {
  ANNOTATIONS_THREAD_COMMENTS,
  useGetThreadsList,
} from '@/features/annotation-management'
import { QUERY_KEY_COMMENT_LIST } from '@/features/comment/constants'
import { useDocumentStore } from '@/features/document'
import { transferComment } from '@/features/view-tfl-uploaded/service'

import { TransferCommentPayLoad } from '../../../types'

export const useTransferComment = () => {
  const { activeDocumentId, getListVersion } = useDocumentStore()
  const listVersion = activeDocumentId ? getListVersion(activeDocumentId) : []

  const sourceVersionId = listVersion[1]?.id ?? ''
  const destinationVersionId = listVersion[0]?.id ?? ''

  // Fetch comments for the second version
  const { data: commentsData } = useGetThreadsList({
    documentVersionId: sourceVersionId,
  })

  const allComments = useMemo(() => {
    return commentsData?.pages?.flatMap((page) => page?.data ?? []) ?? []
  }, [commentsData])

  const openComments = useMemo(
    () => allComments.filter((comment) => comment.thread_status === 'open'),
    [allComments]
  )

  const commentsTransfer = useMemo(
    () => openComments.filter((comment) => comment.transferred_at === null),
    [openComments]
  )

  const isAllCommentTransferred = useMemo(
    () => openComments.every((comment) => comment.transferred_at !== null),
    [openComments]
  )

  return {
    listVersion,
    sourceVersionId,
    destinationVersionId,
    openComments,
    commentsTransfer,
    isAllCommentTransferred,
  }
}

export const useTransferCommentMutation = () => {
  const queryClient = useQueryClient()
  const { sourceVersionId, destinationVersionId } = useTransferComment()

  return useMutation({
    mutationFn: (data: TransferCommentPayLoad) => transferComment(data),
    onSuccess: () => {
      // Refetch thread list comments for the source document version
      queryClient.invalidateQueries({
        queryKey: ANNOTATIONS_THREAD_COMMENTS(['thread-list', sourceVersionId]),
      })
      // Refetch thread list comments for the destination document version
      queryClient.invalidateQueries({
        queryKey: ANNOTATIONS_THREAD_COMMENTS([
          'thread-list',
          destinationVersionId,
        ]),
      })
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === QUERY_KEY_COMMENT_LIST,
      })
    },
  })
}
